import axios from "axios";
import { NavigateFunction } from "react-router-dom";
import { baseURL } from "../globals/constants";
import { showErrorToast } from "../utils/Toast";
import i18next from "i18next";

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogEntry {
  id: string;
  createdAt: string;
  level: LogLevel;
  category: string;
  event: string;
  message: string;
  requestId?: string;
  instance?: string;
  actor?: { userId?: string; role?: string };
  tenantId?: string;
  ids?: Record<string, unknown>;
  durationMs?: number;
  http?: { method?: string; route?: string; path?: string; status?: number; ip?: string };
  meta?: unknown;
}

export interface LogFilters {
  from?: string;
  to?: string;
  level?: string;
  category?: string;
  event?: string;
  tenant?: string;
  user?: string;
  requestId?: string;
  q?: string;
}

export interface LogPage<T> {
  results: T[];
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface LogOverview {
  hours: number;
  levels: { error: number; warn: number; info: number };
  http: { requests: number; denied: number; rateLimited: number };
  security: { loginFailed: number; loginSucceeded: number; passwordChanged: number };
  sms: { sent: number; failed: number; deliveryRate: number | null };
  email: { sent: number; failed: number; deliveryRate: number | null };
  cron: { finished: number; failed: number; latestFailures: LogEntry[] };
  stripe: { received: number; failed: number; rejected: number };
  client: { errors: number };
  perHour: { hour: string; errors: number; warnings: number }[];
  alerts: LogEntry[];
}

export interface AuditEntry {
  id: string;
  createdAt: string;
  actor?: { userId?: string; role?: string; email?: string };
  action: string;
  route?: string;
  target?: Record<string, string>;
  fields?: string[];
  status?: number;
  reason?: string;
  requestId?: string;
  ip?: string;
}

const authHeaders = () => ({
  headers: {
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  },
});

const handleError = (error: any, isMobile: boolean, navigate: NavigateFunction, fallbackMessage: string) => {
  if (error?.response?.status === 401) {
    showErrorToast(i18next.t("messages.sessionExpired"), isMobile);
    localStorage.removeItem("access_token");
    navigate("/");
  } else {
    showErrorToast(error?.response?.data?.message || fallbackMessage, isMobile);
  }
};

// Empty filters are left out of the query string
const clean = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));

export const getLogs = async (
  filters: LogFilters,
  page: number,
  isMobile: boolean,
  navigate: NavigateFunction
): Promise<LogPage<LogEntry> | null> => {
  try {
    const response = await axios.get(`${baseURL}/v1/logs`, { ...authHeaders(), params: clean({ ...filters, page, limit: 50 }) });
    return response.data;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not load the log");
    return null;
  }
};

export const getLogEntry = async (id: string, isMobile: boolean, navigate: NavigateFunction): Promise<LogEntry | null> => {
  try {
    const response = await axios.get(`${baseURL}/v1/logs/${id}`, authHeaders());
    return response.data;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not load the log entry");
    return null;
  }
};

/** Downloads the filtered log as a CSV file */
export const exportLogs = async (filters: LogFilters, isMobile: boolean, navigate: NavigateFunction) => {
  try {
    const response = await axios.get(`${baseURL}/v1/logs/export`, { ...authHeaders(), params: clean({ ...filters }), responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = `breefer-log-${new Date().toISOString().slice(0, 16).replace(":", "")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return true;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not export the log");
    return false;
  }
};

export const getLogOverview = async (hours: number, isMobile: boolean, navigate: NavigateFunction): Promise<LogOverview | null> => {
  try {
    const response = await axios.get(`${baseURL}/v1/logs/overview`, { ...authHeaders(), params: { hours } });
    return response.data;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not load the overview");
    return null;
  }
};

export const getAuditLog = async (
  filters: { from?: string; to?: string; user?: string; q?: string },
  page: number,
  isMobile: boolean,
  navigate: NavigateFunction
): Promise<LogPage<AuditEntry> | null> => {
  try {
    const response = await axios.get(`${baseURL}/v1/logs/audit`, { ...authHeaders(), params: clean({ ...filters, page, limit: 50 }) });
    return response.data;
  } catch (error: any) {
    handleError(error, isMobile, navigate, "Could not load the audit log");
    return null;
  }
};
